import {
  NEXUS_EXPENSE_TYPES,
  NEXUS_RATER_TIERS,
  type NexusAuxiliaryCost,
  type NexusBugCostInput,
  type NexusBugEstimateInput,
  type NexusExpenseType,
  type NexusRaterCost,
  type NexusRaterTier,
} from '../../../../domain/nexus/model';

// Admins type dollars and minutes; Google takes USD cents and seconds.
// Limits enforced by functions/domains/nexus/bugReports.js.
const MAX_CENTS = 10_000_000_000;
const MAX_AHT_SECONDS = 30 * 24 * 60 * 60;
const MAX_VOLUME = 1_000_000_000;
export const MAX_REPORT_LINES = 50;

const USD_RE = /^\d+(\.\d{1,2})?$/;
const NUMBER_RE = /^\d+(\.\d+)?$/;
const WHOLE_RE = /^\d+$/;

// --- Shared ---------------------------------------------------------------------

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** A `datetime-local` value (local time, minute precision) for the given moment. */
export function toLocalDateTimeInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Whole cents from a dollar amount, without floating-point rounding. Accepts "$" and thousands commas. */
export function usdToCents(value: string): number | null {
  const text = value.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!USD_RE.test(text)) return null;
  const [dollars, fraction = ''] = text.split('.');
  return Number(dollars) * 100 + Number(fraction.padEnd(2, '0'));
}

export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export function formatSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  const minutes = seconds / 60;
  return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** Required dollars → cents, or an error message. */
function dollars(value: string, label: string): { cents: number | null; error?: string } {
  if (!value.trim()) return { cents: null, error: `Enter ${label}.` };
  const cents = usdToCents(value);
  if (cents === null) return { cents: null, error: 'Enter a dollar amount such as 1250 or 1250.50.' };
  if (cents > MAX_CENTS) return { cents: null, error: 'The amount is too large.' };
  return { cents };
}

/** Minutes → whole seconds, or an error message. */
function minutesToSeconds(value: string): { seconds: number | null; error?: string } {
  const text = value.trim();
  if (!NUMBER_RE.test(text)) return { seconds: null, error: 'Enter minutes as a number, such as 4 or 4.5.' };
  const seconds = Math.round(Number(text) * 60);
  if (seconds > MAX_AHT_SECONDS) return { seconds: null, error: 'The handling time is too large.' };
  return { seconds };
}

function reportTime(value: string): { iso: string | null; error?: string } {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return { iso: null, error: 'Choose the date and time this report covers.' };
  return { iso: date.toISOString() };
}

// --- Estimate -------------------------------------------------------------------

export interface EstimateForm {
  costUsd: string;
  ahtMinutes: string;
  reportTime: string;
}

export type EstimateFormErrors = Partial<Record<keyof EstimateForm, string>>;

export function emptyEstimateForm(now: Date = new Date()): EstimateForm {
  return { costUsd: '', ahtMinutes: '', reportTime: toLocalDateTimeInput(now) };
}

/** The estimate to send, or the errors to show. Handling time is optional. */
export function parseEstimateForm(form: EstimateForm): { input: NexusBugEstimateInput | null; errors: EstimateFormErrors } {
  const errors: EstimateFormErrors = {};
  const cost = dollars(form.costUsd, 'the estimated total cost in US dollars');
  if (cost.error) errors.costUsd = cost.error;
  let ahtSeconds: number | null = null;
  if (form.ahtMinutes.trim()) {
    const aht = minutesToSeconds(form.ahtMinutes);
    if (aht.error) errors.ahtMinutes = aht.error;
    ahtSeconds = aht.seconds;
  }
  const time = reportTime(form.reportTime);
  if (time.error) errors.reportTime = time.error;

  if (Object.keys(errors).length || cost.cents === null || time.iso === null) return { input: null, errors };
  return { input: { costCents: cost.cents, ahtSeconds, reportTime: time.iso }, errors };
}

export function confirmEstimateMessage(bugId: string, input: NexusBugEstimateInput): string {
  const aht = input.ahtSeconds === null ? '' : ` and an average handling time of ${formatSeconds(input.ahtSeconds)}`;
  return `Send an estimated total cost of ${formatCents(input.costCents)}${aht} to Google for bug ${bugId}?`;
}

// --- Actual cost ----------------------------------------------------------------

export interface RaterCostForm {
  /** Stable React key; never sent. */
  key: string;
  raterTier: NexusRaterTier | '';
  locale: string;
  expenseType: NexusExpenseType | '';
  ahtMinutes: string;
  totalMinutes: string;
  totalVolume: string;
  rateUsd: string;
  costUsd: string;
}

export interface AuxiliaryCostForm {
  key: string;
  costType: string;
  description: string;
  costUsd: string;
}

export interface CostForm {
  reportTime: string;
  costUsd: string;
  discountedUsd: string;
  raterCosts: RaterCostForm[];
  auxiliaryCosts: AuxiliaryCostForm[];
}

type RaterField = Exclude<keyof RaterCostForm, 'key'>;
type AuxiliaryField = Exclude<keyof AuxiliaryCostForm, 'key'>;

export interface CostFormErrors {
  reportTime?: string;
  costUsd?: string;
  discountedUsd?: string;
  /** Set when there are no lines at all. */
  lines?: string;
  raterCosts: Partial<Record<RaterField, string>>[];
  auxiliaryCosts: Partial<Record<AuxiliaryField, string>>[];
}

let nextLineKey = 0;
function lineKey(): string {
  nextLineKey += 1;
  return `line-${nextLineKey}`;
}

export function emptyRaterCost(): RaterCostForm {
  return {
    key: lineKey(), raterTier: '', locale: 'en-us', expenseType: '',
    ahtMinutes: '', totalMinutes: '', totalVolume: '', rateUsd: '', costUsd: '',
  };
}

export function emptyAuxiliaryCost(): AuxiliaryCostForm {
  return { key: lineKey(), costType: '', description: '', costUsd: '' };
}

export function emptyCostForm(now: Date = new Date()): CostForm {
  return { reportTime: toLocalDateTimeInput(now), costUsd: '', discountedUsd: '', raterCosts: [emptyRaterCost()], auxiliaryCosts: [] };
}

/** The rate label depends on how the line is billed. */
export function rateLabel(expenseType: NexusExpenseType | ''): string {
  if (expenseType === 'Time and Materials (T&M)') return 'Rate per hour (USD)';
  if (expenseType === 'Pay Per Unit (PPU)') return 'Rate per task (USD)';
  return 'Rate (USD)';
}

/** What the lines add up to, ignoring amounts that are not yet valid. Shown beside the total. */
export function costLinesTotalCents(form: CostForm): number {
  return [...form.raterCosts, ...form.auxiliaryCosts].reduce((sum, line) => sum + (usdToCents(line.costUsd) ?? 0), 0);
}

function parseRaterCost(line: RaterCostForm): { value: NexusRaterCost | null; errors: Partial<Record<RaterField, string>> } {
  const errors: Partial<Record<RaterField, string>> = {};
  if (!(NEXUS_RATER_TIERS as readonly string[]).includes(line.raterTier)) errors.raterTier = 'Choose a tier.';
  if (!(NEXUS_EXPENSE_TYPES as readonly string[]).includes(line.expenseType)) errors.expenseType = 'Choose an expense type.';
  const locale = line.locale.trim().toLowerCase();
  if (!locale) errors.locale = 'Enter a locale such as en-us.';
  else if (locale.length > 35) errors.locale = 'The locale is too long.';

  const aht = line.ahtMinutes.trim() ? minutesToSeconds(line.ahtMinutes) : { seconds: null, error: 'Enter the average handling time.' };
  if (aht.error) errors.ahtMinutes = aht.error;

  const totalMinutes = Number(line.totalMinutes.trim());
  if (!NUMBER_RE.test(line.totalMinutes.trim())) errors.totalMinutes = 'Enter the total minutes worked.';

  const volumeText = line.totalVolume.trim().replace(/,/g, '');
  const totalVolume = Number(volumeText);
  if (!WHOLE_RE.test(volumeText)) errors.totalVolume = 'Enter a whole number of items, including replications.';
  else if (totalVolume > MAX_VOLUME) errors.totalVolume = 'The volume is too large.';

  const rate = dollars(line.rateUsd, 'the rate');
  if (rate.error) errors.rateUsd = rate.error;
  const cost = dollars(line.costUsd, 'the cost');
  if (cost.error) errors.costUsd = cost.error;

  if (Object.keys(errors).length || aht.seconds === null || rate.cents === null || cost.cents === null) return { value: null, errors };
  return {
    value: {
      raterTier: line.raterTier as NexusRaterTier,
      locale,
      ahtSeconds: aht.seconds,
      totalMinutes,
      totalVolume,
      costRateCents: rate.cents,
      costCents: cost.cents,
      expenseType: line.expenseType as NexusExpenseType,
    },
    errors,
  };
}

function parseAuxiliaryCost(line: AuxiliaryCostForm): { value: NexusAuxiliaryCost | null; errors: Partial<Record<AuxiliaryField, string>> } {
  const errors: Partial<Record<AuxiliaryField, string>> = {};
  const costType = line.costType.trim();
  if (!costType) errors.costType = 'Enter the type, such as Token Cost.';
  else if (costType.length > 120) errors.costType = 'The type is limited to 120 characters.';
  const description = line.description.trim();
  if (description.length > 500) errors.description = 'The description is limited to 500 characters.';
  const cost = dollars(line.costUsd, 'the cost');
  if (cost.error) errors.costUsd = cost.error;
  if (Object.keys(errors).length || cost.cents === null) return { value: null, errors };
  return { value: { costType, description, costCents: cost.cents }, errors };
}

function hasErrors(errors: CostFormErrors): boolean {
  return Boolean(errors.reportTime || errors.costUsd || errors.discountedUsd || errors.lines)
    || errors.raterCosts.some((line) => Object.keys(line).length > 0)
    || errors.auxiliaryCosts.some((line) => Object.keys(line).length > 0);
}

/** The period's cost to send, or the errors to show. Google needs at least one line. */
export function parseCostForm(form: CostForm): { input: NexusBugCostInput | null; errors: CostFormErrors } {
  const raters = form.raterCosts.map(parseRaterCost);
  const auxiliaries = form.auxiliaryCosts.map(parseAuxiliaryCost);
  const errors: CostFormErrors = {
    raterCosts: raters.map((line) => line.errors),
    auxiliaryCosts: auxiliaries.map((line) => line.errors),
  };
  const time = reportTime(form.reportTime);
  if (time.error) errors.reportTime = time.error;
  const cost = dollars(form.costUsd, 'the full-price total for the period');
  if (cost.error) errors.costUsd = cost.error;
  let discountedPriceCents: number | null = null;
  if (form.discountedUsd.trim()) {
    const discounted = dollars(form.discountedUsd, 'the discounted price');
    if (discounted.error) errors.discountedUsd = discounted.error;
    discountedPriceCents = discounted.cents;
  }
  if (!raters.length && !auxiliaries.length) errors.lines = 'Add at least one rater cost or auxiliary cost.';
  else if (raters.length > MAX_REPORT_LINES || auxiliaries.length > MAX_REPORT_LINES) {
    errors.lines = `Each list is limited to ${MAX_REPORT_LINES} lines.`;
  }

  if (hasErrors(errors) || time.iso === null || cost.cents === null) return { input: null, errors };
  return {
    input: {
      reportTime: time.iso,
      costCents: cost.cents,
      discountedPriceCents,
      raterCosts: raters.map((line) => line.value as NexusRaterCost),
      auxiliaryCosts: auxiliaries.map((line) => line.value as NexusAuxiliaryCost),
    },
    errors,
  };
}

export function confirmCostMessage(bugId: string, input: NexusBugCostInput): string {
  const lines = [
    input.raterCosts.length ? plural(input.raterCosts.length, 'rater line') : '',
    input.auxiliaryCosts.length ? plural(input.auxiliaryCosts.length, 'auxiliary line') : '',
  ].filter(Boolean).join(' and ');
  const discounted = input.discountedPriceCents === null ? '' : `, ${formatCents(input.discountedPriceCents)} after discount`;
  return `Send a period cost of ${formatCents(input.costCents)}${discounted} (${lines}) to Google for bug ${bugId}?`;
}
